import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import {
  UserAnalytics,
  UserAnalyticsResponse,
  SortField,
  SortDirection,
} from '../../shared/models';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  constructor(private http: HttpClient, private authService: AuthService) {}

  /**
   * Get authentication headers with token
   */
  private async getAuthHeaders(): Promise<HttpHeaders> {
    console.log('🔐 AnalyticsService: Getting auth headers...');
    const token = await this.authService.getAuthToken();
    console.log('🔐 AnalyticsService: Token result:', !!token);
    console.log('📱 Platform:', Capacitor.getPlatform());
    console.log('🌐 API URL:', environment.apiUrl);
    
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
      console.log('✅ AnalyticsService: Authorization header set');
    } else {
      console.log('⚠️ AnalyticsService: No token available');
    }

    return headers;
  }

  /**
   * Get all users with their word usage statistics (admin only)
   */
  getAllUsers(): Observable<UserAnalytics[]> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<UserAnalyticsResponse>(
          `${environment.apiUrl}/api/admin/users`,
          { headers }
        );
      }),
      switchMap((response) => {
        if (response.success && response.data) {
          return [response.data];
        }
        throw new Error('Failed to fetch user analytics data');
      }),
      catchError((error) => {
        console.error('❌ Error fetching user analytics:', error);
        if (error.status === 403) {
          return throwError(
            () => new Error('Access denied. Admin role required.')
          );
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Sort users by a specific field
   */
  sortUsers(
    users: UserAnalytics[],
    field: SortField,
    direction: SortDirection = 'asc'
  ): UserAnalytics[] {
    const sorted = [...users].sort((a, b) => {
      const aValue = a[field];
      const bValue = b[field];

      // Handle undefined/null values
      if (aValue === undefined || aValue === null) {
        return bValue === undefined || bValue === null ? 0 : (direction === 'asc' ? -1 : 1);
      }
      if (bValue === undefined || bValue === null) {
        return direction === 'asc' ? 1 : -1;
      }

      // For strings, use localeCompare
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // For numbers, use numeric comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return sorted;
  }

  /**
   * Filter users by search term (email or name)
   */
  filterUsers(users: UserAnalytics[], searchTerm: string): UserAnalytics[] {
    if (!searchTerm || searchTerm.trim() === '') {
      return users;
    }

    const term = searchTerm.toLowerCase().trim();
    return users.filter((user) => {
      return (
        user.email.toLowerCase().includes(term) ||
        user.name.toLowerCase().includes(term)
      );
    });
  }
}
